import { RefreshFailedError } from "./api/errors";
import { refreshSession } from "./api/refreshSession";
import { sessionEvents } from "./sessionEvents";
import { tokenStorage, type Tokens } from "./tokenStorage";

/*
 * Single in-flight refresh shared by every API client (axios, Apollo,
 * openapi-fetch integrations). Concurrent callers await the same Promise
 * so N parallel 401s collapse to exactly ONE refresh request.
 *
 * On refresh-token rejection (4xx), `sessionEvents.emitExpired` is
 * dispatched — AuthProvider listens and surfaces the session-expired
 * modal. The Promise is rejected with RefreshFailedError so the original
 * 401 caller can also handle it.
 */
let inFlight: Promise<Tokens> | null = null;

const doRefresh = async (refreshToken: string): Promise<Tokens> => {
  try {
    const tokens = await refreshSession({ refreshToken });
    tokenStorage.setTokens(tokens);
    return tokens;
  } catch (err) {
    if (err instanceof RefreshFailedError) {
      sessionEvents.emitExpired();
    }
    throw err;
  }
};

export const getOrStartRefresh = (): Promise<Tokens> => {
  if (inFlight) return inFlight;

  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    sessionEvents.emitExpired();
    return Promise.reject(
      new RefreshFailedError(401, "No refresh token in storage")
    );
  }

  inFlight = doRefresh(refreshToken).finally(() => {
    inFlight = null;
  });
  return inFlight;
};

/**
 * Test-only helper to clear the in-flight Promise between tests. Do not
 * call from production code — the singleton self-resets on settle.
 */
export const __resetSessionRefreshForTests = (): void => {
  inFlight = null;
};
